import React, { useEffect, useState, useCallback } from 'react';
import { todayIso, fmtDateDMY, syncDateRange } from '../utils/dateUtils';

const toMoney = (v) => Number(v || 0).toFixed(2);
const addDays = (dateStr, days) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function PmsFolioVoidAudit({ token, selectedPropertyId, selectedProperty, role, permissions }) {
  const isAdmin = role === 'admin' || role === 'hotel_admin' || Boolean(permissions?.hotel_admin);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || selectedPropertyId || ''
  }), [token, selectedProperty, selectedPropertyId]);

  const [filters, setFilters] = useState({
    from_date: addDays(todayIso(), -30),
    to_date: todayIso(),
    folio_id: '',
    q: ''
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.set('from_date', filters.from_date);
      if (filters.to_date)   params.set('to_date', filters.to_date);
      if (filters.folio_id)  params.set('folio_id', filters.folio_id);
      if (filters.q && filters.q.trim()) params.set('q', filters.q.trim());
      const res = await fetch(`/api/v1/pms/folio-ledger-void-log?${params.toString()}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load');
      setRows(data.rows || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filters, headers, isAdmin]);

  useEffect(() => { load(); }, [load]);

  if (!isAdmin) {
    return <div style={{ padding: 24, color: '#b91c1c' }}>Admin / hotel_admin permission required to view the folio void log.</div>;
  }

  return (
    <div style={{ padding: '1rem 1.5rem' }}>
      <div style={{ marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, color: '#0F2C4D' }}>Folio Void Audit</h2>
        <p style={{ margin: '0.2rem 0 0', fontSize: 13, color: '#718096' }}>
          Hard-deletes of folio postings via the admin Void action. Every row here is a permanent record of what was removed, by whom, and why.
        </p>
      </div>

      <div style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.8rem 1rem', marginBottom: '1rem', display: 'flex', gap: '0.8rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div><label style={lbl}>From</label><input type="date" value={filters.from_date} onChange={(e) => setFilters(syncDateRange({ ...filters, from_date: e.target.value }, 'from_date', 'to_date', 'from_date'))} style={inp} /></div>
        <div><label style={lbl}>To</label><input type="date" value={filters.to_date} min={filters.from_date || undefined} onChange={(e) => setFilters(syncDateRange({ ...filters, to_date: e.target.value }, 'from_date', 'to_date', 'to_date'))} style={inp} /></div>
        <div><label style={lbl}>Folio ID</label><input type="number" value={filters.folio_id} onChange={(e) => setFilters({ ...filters, folio_id: e.target.value })} style={{ ...inp, width: 120 }} /></div>
        <div style={{ flex: 1, minWidth: 220 }}><label style={lbl}>Search</label>
          <input type="text" placeholder="posting type, remarks, reason, voider..." value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} style={inp} />
        </div>
        <button onClick={load} style={btnSecondary}>Search</button>
      </div>

      {error && <div style={alertError}>{error}</div>}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#0F2C4D', color: '#fff' }}>
              <th style={th}>Voided At</th>
              <th style={th}>Voided By</th>
              <th style={th}>Folio</th>
              <th style={th}>Original Posting</th>
              <th style={{ ...th, textAlign: 'right' }}>Debit</th>
              <th style={{ ...th, textAlign: 'right' }}>Credit</th>
              <th style={th}>Original Remarks</th>
              <th style={th}>Void Reason</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} style={{ padding: '1rem', textAlign: 'center', color: '#718096' }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} style={{ padding: '1.5rem', textAlign: 'center', color: '#718096' }}>No voided postings in this period.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.void_id} style={{ borderTop: '1px solid #edf2f7' }}>
                <td style={td}>
                  {new Date(r.voided_at).toLocaleString('en-IN', { hour12: false })}<br/>
                  <small style={{ color: '#a0aec0' }}>{r.client_ip || ''}</small>
                </td>
                <td style={td}>{r.voided_by_name || `User #${r.voided_by || '?'}`}</td>
                <td style={td}>
                  Folio <strong>#{r.folio_id || '-'}</strong><br/>
                  <small style={{ color: '#718096' }}>Ledger #{r.voided_ledger_id} · {fmtDateDMY(r.posting_date)}</small>
                </td>
                <td style={td}>
                  <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: '#fed7d7', color: '#9b2c2c', fontWeight: 700 }}>{r.posting_type || '-'}</span>
                  {r.revenue_desc && <div style={{ marginTop: 2, color: '#718096', fontSize: 11 }}>{r.revenue_desc}</div>}
                </td>
                <td style={{ ...td, textAlign: 'right', color: Number(r.debit_amount) > 0 ? '#c53030' : '#a0aec0' }}>{Number(r.debit_amount) > 0 ? `₹${toMoney(r.debit_amount)}` : '—'}</td>
                <td style={{ ...td, textAlign: 'right', color: Number(r.credit_amount) > 0 ? '#276749' : '#a0aec0' }}>{Number(r.credit_amount) > 0 ? `₹${toMoney(r.credit_amount)}` : '—'}</td>
                <td style={{ ...td, fontStyle: 'italic', color: '#4a5568' }}>{r.original_remarks || '—'}</td>
                <td style={{ ...td, color: '#1a202c', fontWeight: 500 }}>{r.void_reason || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: '#4a5568', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 };
const inp = { padding: '0.42rem 0.6rem', border: '1px solid #cbd5e0', borderRadius: 5, fontSize: 13, boxSizing: 'border-box' };
const th = { padding: '0.5rem 0.8rem', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'left' };
const td = { padding: '0.55rem 0.8rem', fontSize: 13, color: '#2d3748', verticalAlign: 'top' };
const btnSecondary = { padding: '0.5rem 1rem', background: '#e2e8f0', color: '#2d3748', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13 };
const alertError = { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '0.6rem 0.9rem', color: '#b91c1c', fontSize: 13, margin: '10px 0' };
